import {NgForOf, NgIf} from "@angular/common";
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import {ImageModel} from "@atlas-workspace/shared/models";
import {Swiper} from "swiper/types";

type ImagesInputModel = (ImageModel)[];

@Component({
  selector: 'atl-slider-gallery-view',
  templateUrl: './slider-gallery-view.component.html',
  styleUrls: ['./slider-gallery-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [NgIf, NgForOf],
})
export class SliderGalleryViewComponent implements OnChanges {
  swiper?: Swiper;

  @Input() images: ImagesInputModel = [];
  @Input() activeIndex?: number;
  @Input() set swiperInstance(instance: Swiper) {
    this.swiper = instance;
    this.cd.detectChanges();
  }
  @Input() size = 72;
  @Input() gap = 12;
  @Input() centered = false;
  @Input() scrollable = false;
  @Input() withDetectChanges = true;

  @Output() clickedOnItem = new EventEmitter<number>();
  @Output() clickedOnExtra = new EventEmitter<number>();

  @ViewChild('scrollableElement', { static: true }) scrollableElement!: ElementRef<HTMLElement>;

  constructor(private cd: ChangeDetectorRef) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.images && this.withDetectChanges) {
      queueMicrotask(() => this.cd.detectChanges());
    }
  }

  get previewImages(): ImagesInputModel {
    if (this.scrollable) {
      return this.images;
    }
    return this.images.slice(0, this.extraImageCount ? 4 : 5);
  }

  get extraImageCount(): number {
    if (this.scrollable) {
      return 0;
    }
    return this.images.length > 5 ? this.images.length - 4 : 0;
  }

  scrollLeft(): void {
    const scroller = this.scrollableElement.nativeElement;
    if (scroller) scroller.scrollBy({ left: -this.scrollStep, behavior: 'smooth' });
  }

  scrollRight(): void {
    const scroller = this.scrollableElement.nativeElement;
    if (scroller) scroller.scrollBy({ left: this.scrollStep, behavior: 'smooth' });
  }

  private get scrollStep(): number {
    return this.size + this.gap / 2;
  }

  scrollEndEvent():void {
    this.cd.detectChanges();
  }

  get showPrevArrow(): boolean {
    const scrollLeft = this.scrollableElement.nativeElement.scrollLeft;
    return scrollLeft > 0;
  }

  get showNextArrow(): boolean {
    const scrollLeft = this.scrollableElement.nativeElement.scrollLeft;
    const scrollWidth = this.scrollableElement.nativeElement.scrollWidth;
    const clientWidth = this.scrollableElement.nativeElement.clientWidth;
    return scrollLeft < scrollWidth - clientWidth;
  }

  clickedOnItemHandler(index: number): void {
    const swiper = this.swiper;
    if (swiper) {
      const slideTo = swiper.params.loop ? swiper.slideToLoop : swiper.slideTo;
      slideTo.call(swiper, index, 0, false);
      swiper.update();
    }
    this.clickedOnItem.emit(index);
  }

  clickedOnExtraHandler(index: number): void {
    const swiper = this.swiper;
    if (swiper && swiper.realIndex !== index) {
      const slideTo = swiper.params.loop ? swiper.slideToLoop : swiper.slideTo;
      slideTo.call(swiper, index, 0, false);
      swiper.update();
    }
    this.clickedOnExtra.emit(index);
  }
}
